import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { movies } from "@/lib/content";
import type { Movie } from "@/types/content";

const slideIds = ["an-yong", "bai-ta-zhi-xia", "gui-xu", "chang-an-ye-xing"];

export default function HeroCarousel() {
  const slides = useMemo<Movie[]>(() => {
    const picked = slideIds
      .map((id) => movies.find((movie) => movie.id === id))
      .filter((movie): movie is Movie => Boolean(movie));
    return picked.length > 0 ? picked : movies.slice(0, 3);
  }, []);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const current = slides[active];

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  if (!current) return null;

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点影视剧推荐轮播"
    >
      <div className="relative flex min-h-[640px] w-full flex-col md:min-h-[660px] lg:min-h-[720px]">
        <div key={current.id} className="absolute inset-0 animate-fade-in">
          <img
            src={current.backdrop}
            alt={`${current.title}焦点剧照，${current.genres.join("、")}题材影视剧画面`}
            title={`${current.title} - 剧情解析与推荐`}
            className="h-full w-full animate-slow-zoom object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/10 to-primary-950/55" />

        <div className="relative z-10 flex w-full flex-1 flex-col justify-end px-4 pb-10 pt-28 md:px-8 md:pb-14 lg:px-12">
          <div className="flex w-full flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="w-full max-w-2xl">
              <p className="flex items-center gap-2 font-label text-[11px] font-bold uppercase tracking-[0.3em] text-accent-400">
                <span className="h-px w-8 bg-accent-500" />
                本期焦点 · 剧情解析
              </p>
              <h1 className="mt-4 font-heading text-4xl font-black leading-tight text-background-50 md:text-5xl lg:text-6xl">
                <span className="mb-3 block font-label text-[11px] font-semibold uppercase tracking-[0.34em] text-background-300">
                  奸情影视内容站 / Editor&apos;s Pick
                </span>
                {current.title}
              </h1>
              <p className="mt-3 font-label text-sm uppercase tracking-[0.2em] text-background-300">
                {current.enTitle} · {current.year}
              </p>
              <p className="mt-4 max-w-xl text-base leading-8 text-background-100 md:text-lg">
                {current.tagline}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
                <span className="flex items-center gap-1.5 font-label font-bold text-accent-300">
                  <i className="ri-star-fill text-xs" aria-hidden="true" />
                  {current.rating.toFixed(1)}
                </span>
                <span className="h-3 w-px bg-background-50/25" />
                <span>{current.region}</span>
                <span className="h-3 w-px bg-background-50/25" />
                <span>{current.genres.join(" / ")}</span>
                <span className="h-3 w-px bg-background-50/25" />
                <span>{current.episodes} 集 · {current.duration}</span>
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to={`/detail/${current.id}`}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors whitespace-nowrap hover:bg-accent-400"
                >
                  查看剧情解析
                  <i className="ri-arrow-right-line text-base" aria-hidden="true" />
                </Link>
                <Link
                  to="/category"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-background-50/40 px-6 py-3 text-sm font-medium text-background-50 transition-colors whitespace-nowrap hover:border-background-50 hover:bg-background-50/10"
                >
                  浏览全部剧集
                  <i className="ri-compass-3-line text-base" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="hidden w-full max-w-xs shrink-0 lg:block">
              <div className="flex flex-col">
                {slides.map((slide, index) => (
                  <button
                    key={slide.id}
                    type="button"
                    onClick={() => setActive(index)}
                    className={`group flex items-center gap-3 border-t py-3 text-left transition-colors ${
                      index === active ? "border-accent-500" : "border-background-50/20 hover:border-background-50/50"
                    }`}
                    aria-label={`切换到${slide.title}`}
                    aria-current={index === active}
                  >
                    <span
                      className={`font-label text-xs font-bold ${
                        index === active ? "text-accent-400" : "text-background-400"
                      }`}
                    >
                      0{index + 1}
                    </span>
                    <span
                      className={`font-heading text-sm font-bold ${
                        index === active ? "text-background-50" : "text-background-300 group-hover:text-background-100"
                      }`}
                    >
                      {slide.title}
                    </span>
                    <span className="ml-auto text-[11px] text-background-400">{slide.year}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => setActive(index)}
                  className={`h-1 rounded-full transition-all ${
                    index === active ? "w-8 bg-accent-500" : "w-4 bg-background-50/35 hover:bg-background-50/60"
                  }`}
                  aria-label={`切换到第${index + 1}张焦点图`}
                />
              ))}
              <span className="ml-2 font-label text-xs text-background-300">
                {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setActive((prev) => (prev - 1 + slides.length) % slides.length)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:border-background-50 hover:bg-background-50/10"
                aria-label="上一张焦点图"
              >
                <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setActive((prev) => (prev + 1) % slides.length)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:border-background-50 hover:bg-background-50/10"
                aria-label="下一张焦点图"
              >
                <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}